<template>
  <!-- Asistente flotante de la plataforma (global, App.vue): botón + panel
       con el chat. Lo propio de cada módulo vive en useAssistantDock. -->
  <div class="asd">
    <transition name="asd-panel">
      <!-- v-show: cerrar no pierde la conversación ni los borradores del hilo. -->
      <section
        v-show="isOpen"
        ref="panelRef"
        class="asd-panel"
        role="dialog"
        aria-modal="false"
        :aria-label="$t('assistant.title')"
        @keydown.esc="close"
      >
        <header class="asd-head">
          <button
            v-if="view === 'pending'"
            type="button"
            class="asd-icon-btn"
            :title="$t('assistant.back')"
            :aria-label="$t('assistant.back')"
            @click="setView('chat')"
          >
            <PhCaretLeft :size="20" />
          </button>
          <span v-else class="asd-avatar" aria-hidden="true"><PhSparkle :size="18" weight="fill" /></span>
          <div class="asd-head__text">
            <span class="asd-head__title">{{ view === 'pending' ? $t('assistant.pending.title') : $t('assistant.title') }}</span>
            <span v-if="view === 'pending'" class="asd-head__sub">{{ $t('assistant.title') }}</span>
            <span v-else class="asd-head__sub"><span class="asd-dot" aria-hidden="true"></span>{{ $t('assistant.online') }}</span>
          </div>
          <button
            v-if="view === 'chat'"
            type="button"
            class="asd-icon-btn"
            :title="$t('assistant.chat.newConversation')"
            :aria-label="$t('assistant.chat.newConversation')"
            @click="chatRef?.newConversation?.()"
          >
            <PhNotePencil :size="20" />
          </button>
          <button
            v-if="view === 'chat' && showDrafts"
            type="button"
            class="asd-icon-btn"
            :title="$t('assistant.pending.title')"
            :aria-label="$t('assistant.pending.title')"
            @click="setView('pending')"
          >
            <PhTray :size="20" />
            <span v-if="pendingCount" class="asd-badge asd-badge--corner">{{ pendingCount }}</span>
          </button>
          <button type="button" class="asd-icon-btn" :title="$t('assistant.close')" :aria-label="$t('assistant.close')" @click="close">
            <PhX :size="20" />
          </button>
        </header>

        <!-- El chat se mantiene vivo al ir y volver de "Por revisar". -->
        <keep-alive>
          <AssistantChat
            v-if="view === 'chat'"
            ref="chatRef"
            class="asd-body"
            :context-module="contextModule"
            :upload-module="uploadModule"
            :can-upload="canUpload"
            :read-only="readOnly"
            :pending-count="pendingCount"
            :intent="chatIntent"
            @show-pending="setView('pending')"
            @drafts-changed="refreshPending"
            @navigate="onNavigate"
          />
        </keep-alive>
        <DraftList
          v-if="view === 'pending'"
          class="asd-body"
          @open="openDraft"
          @count="(n) => (pendingCount = n)"
        />
      </section>
    </transition>

    <button
      ref="fabRef"
      type="button"
      class="asd-fab"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="isOpen ? $t('assistant.close') : $t('assistant.open')"
      :title="isOpen ? $t('assistant.close') : $t('assistant.open')"
      @click="toggle"
    >
      <transition name="asd-swap" mode="out-in">
        <PhX v-if="isOpen" key="x" :size="24" />
        <PhSparkle v-else key="s" :size="26" weight="fill" />
      </transition>
      <span v-if="!isOpen && pendingCount" class="asd-badge asd-badge--fab">{{ pendingCount }}</span>
    </button>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { PhSparkle, PhX, PhCaretLeft, PhTray, PhNotePencil } from '@phosphor-icons/vue';
import AssistantChat from './AssistantChat.vue';
import DraftList from './DraftList.vue';
import { useUserStore } from '@/store/userStore';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { Role, hasAdvisorAccess } from '@/composables/useRoles';
import { hasTier } from '@/config/plans';
import { useAssistant } from '@/composables/useAssistant';
import { useAssistantDock, moduleConfig, moduleForPath, DEFAULT_UPLOAD_MODULE } from '@/composables/useAssistantDock';

export default {
  name: 'AssistantDock',
  components: { AssistantChat, DraftList, PhSparkle, PhX, PhCaretLeft, PhTray, PhNotePencil },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { user } = storeToRefs(useUserStore());
    const { getUserCompanyInfo } = useLocalStorage();
    const api = useAssistant();
    const { isOpen, view, intent, close, toggle, setView, consumeIntent } = useAssistantDock();

    const chatRef = ref(null);
    const panelRef = ref(null);
    const fabRef = ref(null);
    const pendingCount = ref(0);
    const chatIntent = ref(null);

    // Módulo de la pantalla actual (contexto del chat) y a dónde va un documento adjunto.
    const contextModule = computed(() => moduleForPath(route.path));
    const uploadModule = computed(() => (moduleConfig(contextModule.value) ? contextModule.value : DEFAULT_UPLOAD_MODULE));

    const role = computed(() => user.value?.rl);
    // Auditor (4): ve borradores pero no actúa (403 ROLE_READ_ONLY).
    const readOnly = computed(() => role.value === Role.Auditor);
    // Subir pide el plan del módulo. Advisor/Staff actúan sobre la empresa asignada.
    const canUpload = computed(() => {
      if (readOnly.value) return false;
      const cfg = moduleConfig(uploadModule.value);
      if (!cfg) return false;
      if (hasAdvisorAccess(role.value)) return true;
      return !cfg.minTier || hasTier(cfg.minTier, getUserCompanyInfo()?.subscriptionType);
    });
    const showDrafts = computed(() => canUpload.value || readOnly.value);

    const refreshPending = async () => {
      if (!showDrafts.value) { pendingCount.value = 0; return; }
      const res = await api.listDrafts({ status: 'pending', limit: 1 });
      if (res.ok) pendingCount.value = res.data?.total ?? 0;
    };
    onMounted(refreshPending);

    // Pedidos desde otras pantallas: { attach: true }, { pending: true }…
    watch(intent, (i) => {
      if (!i) return;
      const taken = consumeIntent();
      if (!taken.pending) chatIntent.value = taken;
    }, { immediate: true });

    const openDraft = async (d) => {
      setView('chat');
      await nextTick();
      chatIntent.value = { id: Date.now(), draftId: d.id };
    };

    const onNavigate = (to) => { close(); router.push(to); };

    // Clic fuera del panel lo cierra (el botón flotante ya alterna solo).
    const onOutside = (e) => {
      if (!isOpen.value) return;
      if (panelRef.value?.contains(e.target) || fabRef.value?.contains(e.target)) return;
      close();
    };
    onMounted(() => document.addEventListener('mousedown', onOutside));
    onBeforeUnmount(() => document.removeEventListener('mousedown', onOutside));

    // Al abrir: foco en el campo de mensaje y conteo al día.
    watch(isOpen, async (v) => {
      if (!v) return;
      refreshPending();
      await nextTick();
      chatRef.value?.focus?.();
    });

    return {
      isOpen, view, chatRef, panelRef, fabRef, pendingCount, chatIntent, contextModule, uploadModule,
      canUpload, readOnly, showDrafts,
      close, toggle, setView, refreshPending, openDraft, onNavigate,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/assistant/assistant-dock";
</style>
